import { useEffect, useMemo, useState, type ReactNode } from 'react'
import { Download, Loader2 } from 'lucide-react'
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { Textarea } from '@/components/ui/textarea'
import { Checkbox } from '@/components/ui/checkbox'
import { Label } from '@/components/ui/label'
import { FormField } from '@/components/FormField'
import { Alert, AlertDescription } from '@/components/ui/alert'
import { LoadingSpinner } from '@/components/LoadingSpinner'
import { Toolbar } from '@/components/Toolbar'
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select'
import { useToast } from '@/hooks/useToast'
import { ApiError } from '@/lib/api'
import { formatDate } from '@/lib/locale'
import { useReportBrand } from '@/lib/pdf'
import { useClientIntake, useSaveClientIntake } from '@/features/clientIntake/queries'
import { useTenantSettings } from '@/features/company/queries'
import type { ClientIntakeForm } from '@/features/clientIntake/types'

type GroupKey = keyof Pick<
  ClientIntakeForm,
  | 'entityType'
  | 'accountingSoftware'
  | 'bookManager'
  | 'paymentMethods'
  | 'payrollProvider'
  | 'payrollFrequency'
  | 'benefits'
  | 'services'
  | 'reportFrequency'
  | 'financialReports'
  | 'commMethod'
  | 'meetingFrequency'
>

type TextKey = keyof Omit<ClientIntakeForm, GroupKey>

const EMPTY: ClientIntakeForm = {}

const ENTITY_TYPES = [
  'Sole Proprietorship',
  'Partnership',
  'LLC',
  'S-Corporation',
  'C-Corporation',
  'Non-Profit',
  'Other',
]

const ACCOUNTING_SOFTWARE = [
  'QuickBooks Desktop',
  'QuickBooks Online',
  'Xero',
  'FreshBooks',
  'Sage',
  'Wave',
  'Excel/Spreadsheets',
  'Paper-based',
  'Other',
]

const BOOK_MANAGERS = [
  'Internal bookkeeper',
  'Business owner',
  'Another accounting firm',
  'No formal bookkeeping system',
  'Other',
]

const PAYMENT_METHODS = [
  'Electronic payments (ACH/EFT)',
  'Credit card processing',
  'PayPal or other processors',
  'Cash transactions',
  'Checks',
]

const PAYROLL_PROVIDERS = [
  'ADP',
  'Paychex',
  'Gusto',
  'QuickBooks Payroll',
  'In-house',
  'Other',
  'N/A',
]

const PAYROLL_FREQUENCIES = ['Weekly', 'Bi-weekly', 'Semi-monthly', 'Monthly', 'Other']

const BENEFITS = [
  'Health Insurance',
  'Retirement Plan (401k, SEP)',
  'Paid Time Off (PTO)',
  'Other',
  'None',
]

const SERVICES_BOOKKEEPING = [
  'Monthly bookkeeping',
  'Quarterly bookkeeping',
  'Annual bookkeeping/Clean-up',
  'Bank & Credit Card Reconciliation',
  'Accounts Receivable Management',
  'Accounts Payable Management',
  'Cash Flow Management & Projections',
]

const SERVICES_PAYROLL = [
  'Full payroll processing',
  'Payroll tax filings & compliance',
  'W-2/1099 preparation',
]

const SERVICES_TAX = [
  'Business tax preparation (Federal & State)',
  'Personal tax preparation (for owners/partners)',
  'Proactive tax planning & strategy',
  'Sales tax compliance & filings',
  'IRS/State representation',
]

const SERVICES_ADVISORY = [
  'Financial statement preparation & analysis',
  'Budgeting & forecasting',
  'Business planning & strategy',
  'Profitability analysis & improvement',
  'KPI development & tracking',
  'Virtual CFO services',
]

const SERVICES_OTHER = [
  'Software setup & training (e.g., QuickBooks)',
  'Audit support & preparation',
  'Industry-specific accounting',
  'Other specialized',
]

const REPORT_FREQUENCIES = [
  'Monthly',
  'Quarterly',
  'Annually',
  'As needed for decisions',
  'Other',
]

const FINANCIAL_REPORTS = [
  'Balance Sheet',
  'Profit & Loss (Income Statement)',
  'Statement of Cash Flows',
  'Budget vs. Actual Analysis',
  'Accounts Receivable Aging',
  'Accounts Payable Aging',
  'Custom reports',
  'None/Not sure yet',
]

const COMM_METHODS = [
  'Email',
  'Phone Calls',
  'Scheduled Video Calls (Zoom, Teams, etc.)',
  'Secure Client Portal',
  'In-person meetings (if local/applicable)',
]

const MEETING_FREQUENCIES = ['Weekly', 'Bi-Weekly', 'Monthly', 'Quarterly', 'As needed']

const TOTAL_STEPS = 9

type CheckboxGroupProps = {
  legend: string
  options: readonly string[]
  values: string[] | undefined
  onToggle: (value: string) => void
  otherValue?: string
  otherText?: string
  onOtherChange?: (value: string) => void
  otherPlaceholder?: string
}

function CheckboxGroup({
  legend,
  options,
  values,
  onToggle,
  otherValue = 'Other',
  otherText,
  onOtherChange,
  otherPlaceholder,
}: CheckboxGroupProps) {
  const selected = values ?? []
  const otherChecked = selected.includes(otherValue)
  return (
    <fieldset className="space-y-2">
      <legend className="text-sm font-medium">{legend}</legend>
      <div className="grid gap-2 sm:grid-cols-2">
        {options.map((opt) => {
          const id = `${legend}-${opt}`.replace(/\s+/g, '-').toLowerCase()
          return (
            <div key={opt} className="flex items-start gap-2">
              <Checkbox
                id={id}
                checked={selected.includes(opt)}
                onCheckedChange={() => onToggle(opt)}
              />
              <Label htmlFor={id} className="font-normal">
                {opt}
              </Label>
            </div>
          )
        })}
      </div>
      {onOtherChange && otherChecked ? (
        <Input
          value={otherText ?? ''}
          onChange={(e) => onOtherChange(e.currentTarget.value)}
          placeholder={otherPlaceholder ?? 'Specify other'}
          className="mt-2 max-w-md"
        />
      ) : null}
    </fieldset>
  )
}

export function BusinessInfoSection() {
  const { toast } = useToast()
  const intakeQ = useClientIntake()
  const tenantQ = useTenantSettings()
  const brand = useReportBrand()
  const saveM = useSaveClientIntake()

  const [form, setForm] = useState<ClientIntakeForm>(EMPTY)
  const [step, setStep] = useState(0)
  const [error, setError] = useState<string | null>(null)
  const [hydrated, setHydrated] = useState(false)
  const [downloading, setDownloading] = useState(false)

  useEffect(() => {
    if (hydrated) return
    if (intakeQ.isSuccess) {
      const data = intakeQ.data?.data
      if (data) setForm(data)
      setHydrated(true)
    }
  }, [intakeQ.isSuccess, intakeQ.data, hydrated])

  const setText = <K extends TextKey>(key: K, value: string) =>
    setForm((prev) => ({ ...prev, [key]: value }))

  const toggleInGroup = (key: GroupKey, value: string) =>
    setForm((prev) => {
      const current = (prev[key] as string[] | undefined) ?? []
      const next = current.includes(value)
        ? current.filter((v) => v !== value)
        : [...current, value]
      return { ...prev, [key]: next }
    })

  const progressPct = ((step + 1) / TOTAL_STEPS) * 100
  const isLast = step === TOTAL_STEPS - 1

  const cleanedPayload = useMemo(() => {
    const payload: ClientIntakeForm = { ...form }
    const clearMap: Array<{ group: GroupKey; textKey: keyof ClientIntakeForm; otherValue?: string }> = [
      { group: 'entityType', textKey: 'entityTypeOther' },
      { group: 'accountingSoftware', textKey: 'accountingSoftwareOther' },
      { group: 'bookManager', textKey: 'bookManagerOther' },
      { group: 'payrollProvider', textKey: 'payrollProviderOther' },
      { group: 'payrollFrequency', textKey: 'payrollFrequencyOther' },
      { group: 'benefits', textKey: 'benefitsOther' },
      { group: 'reportFrequency', textKey: 'reportFrequencyOther' },
      { group: 'services', textKey: 'servicesOtherSpecialized', otherValue: 'Other specialized' },
      { group: 'financialReports', textKey: 'financialReportsCustom', otherValue: 'Custom reports' },
    ]
    for (const { group, textKey, otherValue } of clearMap) {
      const checked = (payload[group] as string[] | undefined) ?? []
      if (!checked.includes(otherValue ?? 'Other')) {
        (payload as Record<string, unknown>)[textKey as string] = ''
      }
    }
    return payload
  }, [form])

  const goNext = () => {
    setError(null)
    if (isLast) {
      void submit()
      return
    }
    setStep((s) => Math.min(s + 1, TOTAL_STEPS - 1))
    window.scrollTo(0, 0)
  }

  const goBack = () => {
    setError(null)
    setStep((s) => Math.max(s - 1, 0))
    window.scrollTo(0, 0)
  }

  const submit = async () => {
    setError(null)
    try {
      await saveM.mutateAsync(cleanedPayload)
      toast({ title: 'Business info saved.' })
    } catch (err) {
      const msg = messageFor(err, 'Failed to save your business info.')
      setError(msg)
      toast({ variant: 'destructive', title: 'Save failed', description: msg })
    }
  }

  const downloadPdf = async () => {
    setDownloading(true)
    try {
      const { downloadBusinessDataPdf } = await import('@/features/clientIntake/export')
      const businessName =
        tenantQ.data?.tenant?.business_name || tenantQ.data?.tenant?.name || ''
      await downloadBusinessDataPdf(cleanedPayload, {
        businessName,
        generatedOn: formatDate(new Date()),
        brand,
      })
    } catch (err) {
      toast({
        variant: 'destructive',
        title: 'Download failed',
        description: messageFor(err, 'Could not generate PDF.'),
      })
    } finally {
      setDownloading(false)
    }
  }

  if (intakeQ.isLoading) {
    return (
      <Card>
        <CardContent className="py-10">
          <LoadingSpinner />
        </CardContent>
      </Card>
    )
  }

  const stepContent: ReactNode[] = [
    // Step 1 - Business Information
    <div key="s1" className="space-y-4">
      <h3 className="text-lg font-semibold">Step 1: Business Information</h3>
      <div className="grid gap-4 md:grid-cols-2">
        <FormField
          label="Legal Business Name"
          value={form.legalBusinessName ?? ''}
          onChange={(e) => setText('legalBusinessName', e.currentTarget.value)}
        />
        <FormField
          label="DBA (if applicable)"
          value={form.dba ?? ''}
          onChange={(e) => setText('dba', e.currentTarget.value)}
        />
        <FormField
          label="Business Address"
          containerClassName="md:col-span-2"
          value={form.address ?? ''}
          onChange={(e) => setText('address', e.currentTarget.value)}
        />
        <FormField
          label="Business Phone"
          type="tel"
          value={form.phone ?? ''}
          onChange={(e) => setText('phone', e.currentTarget.value)}
        />
        <FormField
          label="Website"
          value={form.website ?? ''}
          onChange={(e) => setText('website', e.currentTarget.value)}
        />
        <FormField
          label="Year Established"
          type="number"
          min={1800}
          max={2099}
          value={form.yearEstablished ?? ''}
          onChange={(e) => setText('yearEstablished', e.currentTarget.value)}
        />
        <FormField
          label="Federal EIN"
          value={form.ein ?? ''}
          onChange={(e) => setText('ein', e.currentTarget.value)}
        />
        <FormField
          label="State Tax ID (if applicable)"
          value={form.stateTaxID ?? ''}
          onChange={(e) => setText('stateTaxID', e.currentTarget.value)}
        />
        <FormField
          label="Fiscal Year End (MM/DD)"
          placeholder="MM/DD"
          value={form.fiscalYearEnd ?? ''}
          onChange={(e) => setText('fiscalYearEnd', e.currentTarget.value)}
        />
        <FormField
          label="Primary Industry / NAICS Code"
          value={form.naicsCode ?? ''}
          onChange={(e) => setText('naicsCode', e.currentTarget.value)}
        />
      </div>
      <CheckboxGroup
        legend="Entity Type"
        options={ENTITY_TYPES}
        values={form.entityType}
        onToggle={(v) => toggleInGroup('entityType', v)}
        otherText={form.entityTypeOther}
        onOtherChange={(v) => setText('entityTypeOther', v)}
        otherPlaceholder="Specify other type"
      />
      <FormField
        label="Brief Description of Business Activities"
        control={
          <Textarea
            value={form.businessDescription ?? ''}
            onChange={(e) => setText('businessDescription', e.currentTarget.value)}
          />
        }
      />
    </div>,

    // Step 2 - Key Contacts
    <div key="s2" className="space-y-4">
      <h3 className="text-lg font-semibold">Step 2: Key Contacts</h3>
      <div className="grid gap-4 md:grid-cols-2">
        <FormField
          label="Primary Contact Name"
          value={form.primaryContactName ?? ''}
          onChange={(e) => setText('primaryContactName', e.currentTarget.value)}
        />
        <FormField
          label="Title"
          value={form.primaryContactTitle ?? ''}
          onChange={(e) => setText('primaryContactTitle', e.currentTarget.value)}
        />
        <FormField
          label="Phone"
          type="tel"
          value={form.primaryContactPhone ?? ''}
          onChange={(e) => setText('primaryContactPhone', e.currentTarget.value)}
        />
        <FormField
          label="Email"
          type="email"
          value={form.primaryContactEmail ?? ''}
          onChange={(e) => setText('primaryContactEmail', e.currentTarget.value)}
        />
      </div>
      <hr className="border-border" />
      <div className="grid gap-4 md:grid-cols-2">
        <FormField
          label="Secondary Contact Name (Optional)"
          value={form.secondaryContactName ?? ''}
          onChange={(e) => setText('secondaryContactName', e.currentTarget.value)}
        />
        <FormField
          label="Title (Optional)"
          value={form.secondaryContactTitle ?? ''}
          onChange={(e) => setText('secondaryContactTitle', e.currentTarget.value)}
        />
        <FormField
          label="Phone (Optional)"
          type="tel"
          value={form.secondaryContactPhone ?? ''}
          onChange={(e) => setText('secondaryContactPhone', e.currentTarget.value)}
        />
        <FormField
          label="Email (Optional)"
          type="email"
          value={form.secondaryContactEmail ?? ''}
          onChange={(e) => setText('secondaryContactEmail', e.currentTarget.value)}
        />
      </div>
      <FormField
        label="Other Decision Makers (Names/Titles, Optional)"
        control={
          <Textarea
            value={form.otherDecisionMakers ?? ''}
            onChange={(e) => setText('otherDecisionMakers', e.currentTarget.value)}
          />
        }
      />
    </div>,

    // Step 3 - Current Accounting Setup
    <div key="s3" className="space-y-4">
      <h3 className="text-lg font-semibold">Step 3: Current Accounting Setup</h3>
      <CheckboxGroup
        legend="Current Accounting Software"
        options={ACCOUNTING_SOFTWARE}
        values={form.accountingSoftware}
        onToggle={(v) => toggleInGroup('accountingSoftware', v)}
        otherText={form.accountingSoftwareOther}
        onOtherChange={(v) => setText('accountingSoftwareOther', v)}
        otherPlaceholder="Specify other software"
      />
      <FormField
        label="Software Version / Year (if applicable)"
        value={form.softwareVersion ?? ''}
        onChange={(e) => setText('softwareVersion', e.currentTarget.value)}
      />
      <CheckboxGroup
        legend="Who manages your books currently?"
        options={BOOK_MANAGERS}
        values={form.bookManager}
        onToggle={(v) => toggleInGroup('bookManager', v)}
        otherText={form.bookManagerOther}
        onOtherChange={(v) => setText('bookManagerOther', v)}
        otherPlaceholder="Specify who manages"
      />
      <FormField
        label="How are your financial records organized?"
        control={
          <Textarea
            value={form.financialRecords ?? ''}
            onChange={(e) => setText('financialRecords', e.currentTarget.value)}
          />
        }
      />
      <FormField
        label="Bank Accounts (list institutions)"
        control={
          <Textarea
            value={form.bankAccounts ?? ''}
            onChange={(e) => setText('bankAccounts', e.currentTarget.value)}
          />
        }
      />
      <FormField
        label="Credit Cards (list issuers)"
        control={
          <Textarea
            value={form.creditCards ?? ''}
            onChange={(e) => setText('creditCards', e.currentTarget.value)}
          />
        }
      />
      <CheckboxGroup
        legend="What payment methods do you primarily use/accept?"
        options={PAYMENT_METHODS}
        values={form.paymentMethods}
        onToggle={(v) => toggleInGroup('paymentMethods', v)}
      />
    </div>,

    // Step 4 - Payroll
    <div key="s4" className="space-y-4">
      <h3 className="text-lg font-semibold">Step 4: Payroll Information</h3>
      <div className="grid gap-4 md:grid-cols-2">
        <FormField
          label="Number of Employees (W-2)"
          type="number"
          min={0}
          value={form.employees ?? ''}
          onChange={(e) => setText('employees', e.currentTarget.value)}
        />
        <FormField
          label="Number of Independent Contractors (1099)"
          type="number"
          min={0}
          value={form.contractors ?? ''}
          onChange={(e) => setText('contractors', e.currentTarget.value)}
        />
      </div>
      <CheckboxGroup
        legend="Current Payroll Provider"
        options={PAYROLL_PROVIDERS}
        values={form.payrollProvider}
        onToggle={(v) => toggleInGroup('payrollProvider', v)}
        otherText={form.payrollProviderOther}
        onOtherChange={(v) => setText('payrollProviderOther', v)}
        otherPlaceholder="Specify provider"
      />
      <CheckboxGroup
        legend="Payroll Frequency"
        options={PAYROLL_FREQUENCIES}
        values={form.payrollFrequency}
        onToggle={(v) => toggleInGroup('payrollFrequency', v)}
        otherText={form.payrollFrequencyOther}
        onOtherChange={(v) => setText('payrollFrequencyOther', v)}
        otherPlaceholder="Specify frequency"
      />
      <CheckboxGroup
        legend="Benefits Offered"
        options={BENEFITS}
        values={form.benefits}
        onToggle={(v) => toggleInGroup('benefits', v)}
        otherText={form.benefitsOther}
        onOtherChange={(v) => setText('benefitsOther', v)}
        otherPlaceholder="Specify benefits"
      />
    </div>,

    // Step 5 - Taxes
    <div key="s5" className="space-y-4">
      <h3 className="text-lg font-semibold">Step 5: Tax Information</h3>
      <FormField
        label="Who prepared your last tax return?"
        value={form.lastTaxPreparer ?? ''}
        onChange={(e) => setText('lastTaxPreparer', e.currentTarget.value)}
      />
      <FormField
        label="Have you filed all required tax returns to date?"
        control={
          <Select
            value={form.filedAllReturns ?? ''}
            onValueChange={(v) => setText('filedAllReturns', v)}
          >
            <SelectTrigger>
              <SelectValue placeholder="Select…" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="Yes">Yes</SelectItem>
              <SelectItem value="No">No</SelectItem>
              <SelectItem value="Unsure">Unsure</SelectItem>
            </SelectContent>
          </Select>
        }
      />
      <FormField
        label="Do you have any outstanding tax issues (e.g., notices from IRS/State)?"
        control={
          <Select
            value={form.outstandingTaxIssues ?? ''}
            onValueChange={(v) => setText('outstandingTaxIssues', v)}
          >
            <SelectTrigger>
              <SelectValue placeholder="Select…" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="Yes">Yes</SelectItem>
              <SelectItem value="No">No</SelectItem>
              <SelectItem value="Unsure">Unsure</SelectItem>
            </SelectContent>
          </Select>
        }
      />
      <FormField
        label="Are you current on all tax deposits (Payroll, Sales Tax, etc.)?"
        control={
          <Select
            value={form.currentOnTaxDeposits ?? ''}
            onValueChange={(v) => setText('currentOnTaxDeposits', v)}
          >
            <SelectTrigger>
              <SelectValue placeholder="Select…" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="Yes">Yes</SelectItem>
              <SelectItem value="No">No</SelectItem>
              <SelectItem value="N/A">N/A</SelectItem>
            </SelectContent>
          </Select>
        }
      />
      <FormField
        label="States where you currently collect and remit sales tax:"
        control={
          <Textarea
            value={form.salesTaxStates ?? ''}
            onChange={(e) => setText('salesTaxStates', e.currentTarget.value)}
          />
        }
      />
      <FormField
        label="States where you might have nexus (economic or physical presence):"
        control={
          <Textarea
            value={form.nexusStates ?? ''}
            onChange={(e) => setText('nexusStates', e.currentTarget.value)}
          />
        }
      />
    </div>,

    // Step 6 - Services
    <div key="s6" className="space-y-4">
      <h3 className="text-lg font-semibold">Step 6: Service Needs</h3>
      <p className="text-sm text-muted-foreground">
        Please check all services you are interested in.
      </p>
      <CheckboxGroup
        legend="Bookkeeping Services"
        options={SERVICES_BOOKKEEPING}
        values={form.services}
        onToggle={(v) => toggleInGroup('services', v)}
      />
      <CheckboxGroup
        legend="Payroll Services"
        options={SERVICES_PAYROLL}
        values={form.services}
        onToggle={(v) => toggleInGroup('services', v)}
      />
      <CheckboxGroup
        legend="Tax Services"
        options={SERVICES_TAX}
        values={form.services}
        onToggle={(v) => toggleInGroup('services', v)}
      />
      <CheckboxGroup
        legend="Advisory & CFO Services"
        options={SERVICES_ADVISORY}
        values={form.services}
        onToggle={(v) => toggleInGroup('services', v)}
      />
      <CheckboxGroup
        legend="Other Specialized Services"
        options={SERVICES_OTHER}
        values={form.services}
        onToggle={(v) => toggleInGroup('services', v)}
        otherValue="Other specialized"
        otherText={form.servicesOtherSpecialized}
        onOtherChange={(v) => setText('servicesOtherSpecialized', v)}
        otherPlaceholder="Specify other needs"
      />
    </div>,

    // Step 7 - Reporting
    <div key="s7" className="space-y-4">
      <h3 className="text-lg font-semibold">Step 7: Reporting Needs</h3>
      <CheckboxGroup
        legend="How frequently do you need/review financial reports?"
        options={REPORT_FREQUENCIES}
        values={form.reportFrequency}
        onToggle={(v) => toggleInGroup('reportFrequency', v)}
        otherText={form.reportFrequencyOther}
        onOtherChange={(v) => setText('reportFrequencyOther', v)}
        otherPlaceholder="Specify frequency"
      />
      <CheckboxGroup
        legend="Which financial reports are most important to you?"
        options={FINANCIAL_REPORTS}
        values={form.financialReports}
        onToggle={(v) => toggleInGroup('financialReports', v)}
        otherValue="Custom reports"
        otherText={form.financialReportsCustom}
        onOtherChange={(v) => setText('financialReportsCustom', v)}
        otherPlaceholder="Describe custom reports"
      />
      <FormField
        label="Are there specific business metrics or KPIs you track or want to track?"
        control={
          <Textarea
            value={form.businessMetrics ?? ''}
            onChange={(e) => setText('businessMetrics', e.currentTarget.value)}
          />
        }
      />
    </div>,

    // Step 8 - Timeline & Communication
    <div key="s8" className="space-y-4">
      <h3 className="text-lg font-semibold">Step 8: Timeline &amp; Communication</h3>
      <FormField
        label="When would you ideally like services to begin?"
        placeholder="e.g., ASAP, Next Month, Specific Date"
        value={form.startDate ?? ''}
        onChange={(e) => setText('startDate', e.currentTarget.value)}
      />
      <CheckboxGroup
        legend="Preferred communication method(s)"
        options={COMM_METHODS}
        values={form.commMethod}
        onToggle={(v) => toggleInGroup('commMethod', v)}
      />
      <CheckboxGroup
        legend="Preferred meeting / check-in frequency"
        options={MEETING_FREQUENCIES}
        values={form.meetingFrequency}
        onToggle={(v) => toggleInGroup('meetingFrequency', v)}
      />
      <FormField
        label="Best times/days for scheduled meetings or calls"
        value={form.meetingTimes ?? ''}
        onChange={(e) => setText('meetingTimes', e.currentTarget.value)}
      />
    </div>,

    // Step 9 - Additional
    <div key="s9" className="space-y-4">
      <h3 className="text-lg font-semibold">Step 9: Additional Information</h3>
      <FormField
        label="How did you hear about our firm?"
        value={form.referral ?? ''}
        onChange={(e) => setText('referral', e.currentTarget.value)}
      />
      <FormField
        label="What are your top 1-3 accounting or financial pain points right now?"
        control={
          <Textarea
            rows={3}
            value={form.painPoints ?? ''}
            onChange={(e) => setText('painPoints', e.currentTarget.value)}
          />
        }
      />
      <FormField
        label="What are your main goals for working with an accounting firm at this time?"
        control={
          <Textarea
            rows={3}
            value={form.goals ?? ''}
            onChange={(e) => setText('goals', e.currentTarget.value)}
          />
        }
      />
      <FormField
        label="Is there anything else you'd like us to know?"
        control={
          <Textarea
            rows={4}
            value={form.otherInfo ?? ''}
            onChange={(e) => setText('otherInfo', e.currentTarget.value)}
          />
        }
      />
    </div>,
  ]

  return (
    <div className="space-y-4">
      {error ? (
        <Alert variant="destructive">
          <AlertDescription>{error}</AlertDescription>
        </Alert>
      ) : null}

      <Card>
        <CardHeader>
          <CardTitle>
            Step {step + 1} of {TOTAL_STEPS}
          </CardTitle>
          <p className="text-sm text-muted-foreground">
            Capture the business details, contacts, and service preferences for this workspace.
            Your responses are saved per workspace and visible to every team member with the
            permission.
          </p>
          <div className="mt-2 h-2 w-full overflow-hidden rounded-full bg-muted">
            <div
              className="h-full bg-primary transition-all duration-200"
              style={{ width: `${progressPct}%` }}
            />
          </div>
        </CardHeader>
        <CardContent className="space-y-6">{stepContent[step]}</CardContent>
      </Card>

      <Toolbar className="justify-between">
        <Button
          type="button"
          variant="secondary"
          onClick={goBack}
          disabled={step === 0 || saveM.isPending}
        >
          Back
        </Button>
        <div className="flex flex-wrap gap-2">
          <Button
            type="button"
            variant="outline"
            onClick={downloadPdf}
            disabled={downloading || !hydrated}
          >
            {downloading ? (
              <Loader2 className="size-4 animate-spin" />
            ) : (
              <Download className="size-4" />
            )}
            Download PDF
          </Button>
          <Button
            type="button"
            variant="outline"
            onClick={submit}
            disabled={saveM.isPending}
          >
            {saveM.isPending ? <Loader2 className="size-4 animate-spin" /> : null}
            Save / Update
          </Button>
          <Button type="button" onClick={goNext} disabled={saveM.isPending}>
            {isLast && saveM.isPending ? <Loader2 className="size-4 animate-spin" /> : null}
            {isLast ? 'Submit' : 'Next'}
          </Button>
        </div>
      </Toolbar>
    </div>
  )
}

function messageFor(err: unknown, fallback: string): string {
  if (err instanceof ApiError && err.body && typeof err.body === 'object' && 'error' in err.body) {
    const m = (err.body as { error: unknown }).error
    if (typeof m === 'string' && m) return m
  }
  if (err instanceof Error && err.message) return err.message
  return fallback
}
